<script lang="ts" module>
  // Switch — the design-system on/off toggle-switch primitive
  // (wiki/feature-design-system.md §Component primitives). An iOS-style pill
  // track + sliding knob for a binary on/off setting (e.g. the recording
  // panel's background-blur control). Distinct from SegmentedToggle (which
  // picks one of N labelled options) and Select (a compact dropdown): Switch
  // is the canonical TWO-state on/off control where the visual state IS the
  // value — ON = accent-filled track + knob right, OFF = hairline track + knob
  // left. Renders a real `<button role="switch">` so keyboard (Enter/Space) and
  // assistive tech get native semantics; the chrome stays token-driven. Mirrors
  // Select's prop shape — a bindable `checked`, an `onChange`, `disabled`, an
  // accessible label, and a `class` — plus an explicit `data-testid`/`title`
  // passthrough (à la IconButton) so it drops in interchangeably.
</script>

<script lang="ts">
  interface Props {
    /** The on/off state (bindable). */
    checked: boolean;
    /** Fired after the state flips, with the new value. */
    onChange?: (next: boolean) => void;
    /** Disable the control. */
    disabled?: boolean;
    /** Accessible label (default "Toggle"). */
    'aria-label'?: string;
    /** Id of the element that labels this control — wins over `aria-label`. */
    'aria-labelledby'?: string;
    /** Id of the element that describes this control. */
    'aria-describedby'?: string;
    /** Native title tooltip. */
    title?: string;
    /** Test hook — lands on the rendered button. */
    'data-testid'?: string;
    class?: string;
  }

  // eslint-plugin-svelte 3.23.0 `no-unused-props` misses a quoted prop key whose
  // destructure carries a default: getUsedPropertyNamesFromPattern reads a Literal
  // key only when the value is a bare Identifier, never an AssignmentPattern. The
  // default lives at the use site until that is fixed upstream.
  let {
    checked = $bindable(),
    onChange,
    disabled = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    title,
    'data-testid': dataTestid,
    class: klass = '',
  }: Props = $props();

  // Flip on activate: write `checked` (so `bind:checked` callers stay in sync)
  // and forward the new value out via onChange.
  function toggle(): void {
    if (disabled) return;
    const next = !checked;
    checked = next;
    onChange?.(next);
  }
</script>

<button
  type="button"
  role="switch"
  class="vd-switch {klass}"
  class:vd-switch--on={checked}
  aria-checked={checked}
  aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? 'Toggle')}
  aria-labelledby={ariaLabelledBy}
  aria-describedby={ariaDescribedBy}
  {title}
  data-testid={dataTestid}
  {disabled}
  onclick={toggle}
>
  <span class="vd-switch__knob" aria-hidden="true"></span>
</button>

<style lang="scss">
  .vd-switch {
    display: inline-flex;
    align-items: center;
    flex: none;
    width: 44px;
    height: 26px;
    padding: 0;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    cursor: pointer;
    transition:
      background 0.2s ease,
      border-color 0.2s ease;

    &:hover:not(:disabled) {
      border-color: color-mix(in srgb, var(--accent) 45%, var(--line));
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  .vd-switch__knob {
    display: block;
    width: 20px;
    height: 20px;
    margin: 0 2px;
    border-radius: 50%;
    background: var(--ink-mute);
    // Slide the knob to the right when ON.
    transform: translateX(0);
    transition:
      transform 0.2s ease,
      background 0.2s ease;
  }

  // ON = accent-filled track, knob slid right with an --on-accent dot.
  .vd-switch--on {
    background: var(--accent);
    border-color: var(--accent);

    .vd-switch__knob {
      background: var(--on-accent);
      transform: translateX(18px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .vd-switch,
    .vd-switch__knob {
      transition: none;
    }
  }
</style>
